import * as React from "react";
import { useSearchParams } from "react-router";

import { SPEND_PAGE_SIZE } from "@/api/spend.constraints";
import type { SpendDirection, SpendPeriodType, SpendSort } from "@/types/spend";

const PERIODS = new Set<SpendPeriodType>(["calendar", "fiscal"]);
const SORTS = new Set<SpendSort>(["name", "family_count", "estimated", "actual"]);
const DIRECTIONS = new Set<SpendDirection>(["asc", "desc"]);

const integer = (value: string | null, fallback: number, min = 0) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

export const parseSpendQueryState = (params: URLSearchParams, defaultPeriod: SpendPeriodType, currentYear: number) => {
  const periodValue = params.get("period_type") as SpendPeriodType | null;
  const sortValue = params.get("sort") as SpendSort | null;
  const directionValue = params.get("direction") as SpendDirection | null;
  return {
    periodType: periodValue && PERIODS.has(periodValue) ? periodValue : defaultPeriod,
    year: integer(params.get("year"), currentYear, 1),
    ownerUserId: params.get("owner_user_id") ?? "",
    category: params.get("category") ?? "",
    search: params.get("search") ?? "",
    sort: sortValue && SORTS.has(sortValue) ? sortValue : ("name" as const),
    direction: directionValue && DIRECTIONS.has(directionValue) ? directionValue : ("asc" as const),
    offset: integer(params.get("offset"), 0),
    familyId: params.get("family_id") || "all",
    view: params.get("view") === "year" ? ("year" as const) : ("month" as const),
  };
};

export const useSpendQueryState = (defaultPeriod: SpendPeriodType = "calendar") => {
  const [params, setParams] = useSearchParams();
  const state = parseSpendQueryState(params, defaultPeriod, new Date().getFullYear());

  const update = React.useCallback(
    (changes: Record<string, string | number | null | undefined>, resetOffset = false) => {
      setParams(
        previous => {
          const next = new URLSearchParams(previous);
          if (resetOffset) next.delete("offset");
          Object.entries(changes).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "" || (key === "offset" && value === 0))
              next.delete(key);
            else next.set(key, String(value));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { ...state, pageSize: SPEND_PAGE_SIZE, update };
};

export const spendQueryString = (params: URLSearchParams, overrides: Record<string, string>) => {
  const next = new URLSearchParams(params);
  Object.entries(overrides).forEach(([key, value]) => next.set(key, value));
  next.delete("offset");
  return next.toString();
};
